const VISUALIZATION_TYPES = new Set(["line_chart", "bar_chart", "pie_chart", "table"]);
const VISUALIZATION_STATUSES = new Set(["ok", "insufficient_data", "unsupported"]);
const VALUE_FORMATS = new Set(["currency", "percent", "integer", "decimal", "compact"]);

const MAX_TITLE_LENGTH = 120;
const MAX_DESCRIPTION_LENGTH = 320;
const MAX_REASON_LENGTH = 320;
const MAX_DATA_ROWS = 100;
const MAX_SERIES = 6;
const MAX_COLUMNS = 12;
const MAX_STRING_VALUE_LENGTH = 240;
const MAX_KEY_LENGTH = 64;

const SAFE_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
const UNSAFE_KEY_PATTERN = /^(?:html|jsx|script|scripts|component|components|render|children|dangerouslySetInnerHTML|style|className)$/i;
const EVENT_HANDLER_KEY_PATTERN = /^on[A-Z]/;
const UNSAFE_STRING_PATTERN =
  /<\s*\/?\s*(?:script|style|iframe|object|embed|svg|img|video|audio|link|meta|base|form|input|button|a|div|span|html|body|canvas|foreignObject)\b|<\s*[A-Z][A-Za-z0-9]*\b|on[a-z]+\s*=|javascript\s*:|data\s*:\s*text\/html|dangerouslySetInnerHTML|React\.createElement|eval\s*\(|function\s*\(|import\s*\(|=>/i;

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isPrimitiveJsonValue(value) {
  return value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function isSafeString(value, maxLength = MAX_STRING_VALUE_LENGTH) {
  if (typeof value !== "string") return false;
  if (value.length > maxLength) return false;
  return !UNSAFE_STRING_PATTERN.test(value);
}

function isSafeKey(value) {
  if (typeof value !== "string") return false;
  if (!SAFE_KEY_PATTERN.test(value)) return false;
  if (UNSAFE_KEY_PATTERN.test(value)) return false;
  if (EVENT_HANDLER_KEY_PATTERN.test(value)) return false;
  return true;
}

function hasUnsafePayload(value, seen = new Set()) {
  if (typeof value === "function" || typeof value === "symbol" || typeof value === "bigint") return true;
  if (typeof value === "string") return UNSAFE_STRING_PATTERN.test(value);
  if (!value || typeof value !== "object") return false;
  if (seen.has(value)) return true;
  seen.add(value);

  if (Array.isArray(value)) {
    return value.some((item) => hasUnsafePayload(item, seen));
  }

  return Object.entries(value).some(([key, item]) => {
    if (UNSAFE_KEY_PATTERN.test(key) || EVENT_HANDLER_KEY_PATTERN.test(key)) return true;
    return hasUnsafePayload(item, seen);
  });
}

function trimString(value, maxLength) {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text || !isSafeString(text, maxLength)) return null;
  return text.length > maxLength ? text.slice(0, maxLength).trim() : text;
}

function normalizeFormat(value) {
  return VALUE_FORMATS.has(value) ? value : undefined;
}

function normalizeEvidenceRefs(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === "number" && Number.isFinite(item)) return String(item);
      if (typeof item !== "string") return null;
      const ref = item.trim();
      return ref && isSafeString(ref, 96) ? ref : null;
    })
    .filter(Boolean)
    .slice(0, 20);
}

function normalizeSeries(rawSeries) {
  if (!Array.isArray(rawSeries) || rawSeries.length < 1 || rawSeries.length > MAX_SERIES) {
    return null;
  }

  const normalized = [];
  for (const item of rawSeries) {
    if (!isPlainObject(item)) return null;
    const key = trimString(item.key, MAX_KEY_LENGTH);
    const label = trimString(item.label, MAX_TITLE_LENGTH);
    if (!key || !isSafeKey(key) || !label) return null;
    normalized.push({
      key,
      label,
      ...(normalizeFormat(item.format) ? { format: normalizeFormat(item.format) } : {}),
    });
  }
  return normalized;
}

function normalizeColumns(rawColumns) {
  if (!Array.isArray(rawColumns) || rawColumns.length < 1 || rawColumns.length > MAX_COLUMNS) {
    return null;
  }

  const normalized = [];
  for (const item of rawColumns) {
    if (!isPlainObject(item)) return null;
    const key = trimString(item.key, MAX_KEY_LENGTH);
    const label = trimString(item.label, MAX_TITLE_LENGTH);
    if (!key || !isSafeKey(key) || !label) return null;
    normalized.push({
      key,
      label,
      ...(normalizeFormat(item.format) ? { format: normalizeFormat(item.format) } : {}),
    });
  }
  return normalized;
}

function validateDataRows(rawData) {
  if (!Array.isArray(rawData) || rawData.length > MAX_DATA_ROWS) return null;

  const rows = [];
  for (const row of rawData) {
    if (!isPlainObject(row)) return null;
    const safeRow = {};
    for (const [key, value] of Object.entries(row)) {
      if (!isSafeKey(key)) return null;
      if (!isPrimitiveJsonValue(value)) return null;
      if (typeof value === "number" && !Number.isFinite(value)) return null;
      if (typeof value === "string" && !isSafeString(value)) return null;
      safeRow[key] = value;
    }
    rows.push(safeRow);
  }
  return rows;
}

function pickRowKeys(row, keys) {
  return keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      acc[key] = row[key];
    }
    return acc;
  }, {});
}

function hasRequiredKeys(rows, keys) {
  return rows.every((row) => keys.every((key) => Object.prototype.hasOwnProperty.call(row, key)));
}

function hasNumericSeriesValues(rows, series) {
  return rows.every((row) =>
    series.every((item) => row[item.key] == null || (typeof row[item.key] === "number" && Number.isFinite(row[item.key])))
  );
}

function invalid(reason) {
  return { ok: false, reason, visualization: null };
}

export function validateEdenVisualizationSpec(rawSpec) {
  if (!isPlainObject(rawSpec)) return invalid("Visualization must be an object.");
  if (hasUnsafePayload(rawSpec)) return invalid("Visualization contains unsafe rendering payload.");

  const type = rawSpec.type;
  const status = rawSpec.status || "ok";
  if (!VISUALIZATION_TYPES.has(type)) return invalid("Unsupported visualization type.");
  if (!VISUALIZATION_STATUSES.has(status)) return invalid("Unsupported visualization status.");

  const title = trimString(rawSpec.title, MAX_TITLE_LENGTH);
  const description = trimString(rawSpec.description, MAX_DESCRIPTION_LENGTH);
  if (!title) return invalid("Visualization title is required.");
  if (!description) return invalid("Visualization description is required.");

  const data = validateDataRows(rawSpec.data);
  if (!data) return invalid("Visualization data must be an array of primitive JSON rows.");

  const base = {
    type,
    status,
    title,
    description,
    reason: null,
    evidenceRefs: normalizeEvidenceRefs(rawSpec.evidenceRefs),
  };

  if (status !== "ok") {
    const reason = trimString(rawSpec.reason || description, MAX_REASON_LENGTH);
    return {
      ok: true,
      visualization: {
        ...base,
        reason: reason || "The visualization is not available for this answer.",
        data: [],
      },
    };
  }

  if (type === "line_chart" || type === "bar_chart") {
    const xKey = trimString(rawSpec.xKey, MAX_KEY_LENGTH);
    if (!xKey || !isSafeKey(xKey)) return invalid("xKey is required for line and bar charts.");
    const series = normalizeSeries(rawSpec.series);
    if (!series) return invalid("Chart series are required.");
    const requiredKeys = [xKey, ...series.map((item) => item.key)];
    if (!hasRequiredKeys(data, requiredKeys)) return invalid("Chart data is missing required series keys.");
    if (!hasNumericSeriesValues(data, series)) return invalid("Chart series values must be numbers or null.");
    return {
      ok: true,
      visualization: {
        ...base,
        xKey,
        categoryKey: null,
        series,
        data: data.map((row) => pickRowKeys(row, requiredKeys)),
      },
    };
  }

  if (type === "pie_chart") {
    const categoryKey = trimString(rawSpec.categoryKey, MAX_KEY_LENGTH);
    if (!categoryKey || !isSafeKey(categoryKey)) return invalid("categoryKey is required for pie charts.");
    const series = normalizeSeries(rawSpec.series);
    if (!series) return invalid("Pie chart series is required.");
    const firstSeries = [series[0]];
    const requiredKeys = [categoryKey, firstSeries[0].key];
    if (!hasRequiredKeys(data, requiredKeys)) return invalid("Pie chart data is missing category or value keys.");
    if (!hasNumericSeriesValues(data, firstSeries)) return invalid("Pie chart values must be numbers or null.");
    return {
      ok: true,
      visualization: {
        ...base,
        categoryKey,
        series: firstSeries,
        data: data.map((row) => pickRowKeys(row, requiredKeys)),
      },
    };
  }

  const columns = normalizeColumns(rawSpec.columns);
  if (!columns) return invalid("Table columns are required.");
  const requiredKeys = columns.map((item) => item.key);
  if (!hasRequiredKeys(data, requiredKeys)) return invalid("Table data is missing required column keys.");

  return {
    ok: true,
    visualization: {
      ...base,
      columns,
      data: data.map((row) => pickRowKeys(row, requiredKeys)),
    },
  };
}

export function normalizeEdenVisualizations(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => validateEdenVisualizationSpec(item))
    .filter((result) => result.ok && result.visualization)
    .map((result) => result.visualization)
    .slice(0, 4);
}

export const edenVisualizationValidator = {
  normalizeEdenVisualizations,
  validateEdenVisualizationSpec,
};

export default edenVisualizationValidator;
